import { Controller } from "@hotwired/stimulus"

// Watches a post being written and reports how it came to be: seconds of
// active typing, keystrokes, characters pasted in, deletions, the longest
// single insertion, and whether the keyboard was a touch one. The report
// rides along with the form as the post's provenance - the second human
// signal beside the classifier - and tells a post typed out in full, by key
// or by finger, from one that was brought in. Nothing is sent until the
// writer shares.
//
// The counts are kept in the browser beside the draft (see the draft
// controller, which shares the key), so leaving the page and coming back to
// finish does not lose how the words arrived; and they go to the server with
// the draft, so finishing on another device does not lose them either (the
// page brings them back as the saved value). Sharing, or starting over,
// forgets them; so does clearing the field, which is a fresh start.
export default class extends Controller {
  static targets = ["seconds", "keystrokes", "pasted", "deletions", "longest", "touch"]
  static values = { key: String, saved: Object }

  // A pause longer than this is thinking, or leaving; it is not counted as writing.
  static IDLE_MS = 8000

  connect() {
    this.reset()
    this.touch = matchMedia("(pointer: coarse)").matches || navigator.maxTouchPoints > 0
    this.restore()
  }

  reset() {
    this.activeMs = 0
    this.keystrokes = 0
    this.pasted = 0
    this.deletions = 0
    this.longest = 0
    this.lastInputAt = null
    this.lastLength = null
  }

  input(event) {
    const length = event.target.value.length

    // The draft controller putting saved words back fires a plain input
    // event with no inputType before anything has been typed on this page:
    // the words are accounted for by the counts restored with them.
    if (this.lastLength === null && !event.inputType) {
      this.lastLength = length
      if (length === 0) this.reset()
      this.report()
      return
    }

    // An empty field is a fresh start, whatever came before.
    if (length === 0) {
      this.reset()
      this.lastLength = 0
      this.report()
      this.keep()
      return
    }

    const now = performance.now()
    if (this.lastInputAt !== null) {
      const gap = now - this.lastInputAt
      if (gap < this.constructor.IDLE_MS) this.activeMs += gap
    }
    this.lastInputAt = now

    const delta = this.lastLength === null ? length : length - this.lastLength
    this.lastLength = length

    switch (event.inputType) {
      case "insertFromPaste":
      case "insertFromDrop":
      case "insertFromYank":
        this.pasted += Math.max(delta, 0)
        break
      case "deleteContentBackward":
      case "deleteContentForward":
      case "deleteByCut":
      case "deleteWordBackward":
      case "deleteWordForward":
      case "deleteSoftLineBackward":
      case "deleteHardLineBackward":
        this.deletions += 1
        break
      default:
        // Typing, a line break, a spell-check replacement, an autocorrect, a
        // word from the phone's suggestions. The longest of these is kept:
        // a whole sentence arriving at once was not typed, whatever it says.
        if (delta > 0) {
          this.keystrokes += 1
          if (delta > this.longest) this.longest = delta
        } else if (delta < 0) {
          this.deletions += 1
        }
    }

    this.report()
    this.keep()
  }

  // Turbo tells us the form went; a success means the words were shared.
  submitted(event) {
    if (event.detail?.success) this.forget()
  }

  report() {
    this.secondsTarget.value = Math.round(this.activeMs / 1000)
    this.keystrokesTarget.value = this.keystrokes
    this.pastedTarget.value = this.pasted
    this.deletionsTarget.value = this.deletions
    if (this.hasLongestTarget) this.longestTarget.value = this.longest
    if (this.hasTouchTarget) this.touchTarget.value = this.touch ? "1" : "0"
  }

  // --- kept beside the draft ------------------------------------------------

  keep() {
    if (!this.keyValue) return
    const state = { activeMs: this.activeMs, keystrokes: this.keystrokes, pasted: this.pasted,
                    deletions: this.deletions, longest: this.longest }
    try { localStorage.setItem(this.storageKey, JSON.stringify(state)) } catch { /* not kept; the report is what it is */ }
  }

  // This device's counts first; else the server's, which came with its draft.
  restore() {
    if (!this.keyValue) return
    let state
    try { state = JSON.parse(localStorage.getItem(this.storageKey)) } catch { state = null }
    if (!state && this.hasSavedValue && Object.keys(this.savedValue).length > 0) state = this.savedValue
    if (!state) return
    this.activeMs = Number(state.activeMs) || 0
    this.keystrokes = Number(state.keystrokes) || 0
    this.pasted = Number(state.pasted) || 0
    this.deletions = Number(state.deletions) || 0
    this.longest = Number(state.longest) || 0
    this.report()
  }

  forget() {
    try { localStorage.removeItem(this.storageKey) } catch { /* nothing to forget */ }
  }

  get storageKey() {
    return `substance:composition:${this.keyValue}`
  }
}
